import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../../../../env";
import { HugeiconsIcon } from "@hugeicons/react";
import { Building04Icon, PinLocation01Icon } from "@hugeicons/core-free-icons";

const DeliveryMissingReportPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState(null);
  const [expired, setExpired] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [checked, setChecked] = useState({});
  const [qtyByItem, setQtyByItem] = useState({});
  const [selfie, setSelfie] = useState(null);
  const [productPhoto, setProductPhoto] = useState(null);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/delivery-notes/public/missing/${token}`,
        );
        setNote(res.data?.data ?? null);
      } catch {
        setExpired(true);
      } finally {
        setLoading(false);
      }
    };
    fetchNote();
  }, [token]);

  const toggleChecked = (materialId) => {
    setChecked((prev) => ({ ...prev, [materialId]: !prev[materialId] }));
  };

  const setQty = (materialId, value) => {
    setQtyByItem((prev) => ({ ...prev, [materialId]: value }));
  };

  const handleSubmit = async () => {
    const items = Object.keys(checked)
      .filter((materialId) => checked[materialId])
      .map((materialId) => ({
        materialId,
        missingQty: Number(qtyByItem[materialId]),
      }));

    if (items.length === 0) {
      toast.error("Select at least one missing item.");
      return;
    }

    const invalid = items.find(
      (item) => !Number.isInteger(item.missingQty) || item.missingQty < 1,
    );
    if (invalid) {
      toast.error("Enter a valid missing quantity for each selected item.");
      return;
    }

    if (!selfie) {
      toast.error("Please attach a selfie.");
      return;
    }

    if (!productPhoto) {
      toast.error("Please attach a photo of the missing product.");
      return;
    }

    const formData = new FormData();
    formData.append("items", JSON.stringify(items));
    formData.append("selfie", selfie);
    formData.append("productPhoto", productPhoto);

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/delivery-notes/public/missing/${token}/report`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSubmitted(true);
      toast.success("Missing item report submitted.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to submit report.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  if (expired || !note) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">
            Link no longer active
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            This missing-item report link has expired or was already used.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">
            Thank you
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Your missing item report has been submitted successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 py-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h1 className="text-lg font-semibold text-gray-900">
          Report Missing Items
        </h1>
        <p className="mt-1 text-xs text-gray-500">
          {note.company} · {note.deliveryNoteNo}
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-indigo-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="grid size-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
                <HugeiconsIcon icon={Building04Icon} strokeWidth={2} className="size-5" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                Company Address
              </span>
            </div>
            <p className="mt-2.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
              {note.companyAddress || "-"}
            </p>
          </div>
          <div className="rounded-xl border border-indigo-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="grid size-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
                <HugeiconsIcon icon={PinLocation01Icon} strokeWidth={2} className="size-5" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                Supply To
              </span>
            </div>
            <p className="mt-2.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
              {note.supplyTo || "-"}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {note.materials.map((item) => (
            <div
              key={item._id}
              className="rounded-xl border border-gray-200 bg-gray-50 p-3"
            >
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={Boolean(checked[item._id])}
                  onChange={() => toggleChecked(item._id)}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-800">
                    {item.description}
                  </div>
                  <div className="text-xs text-gray-500">
                    Ordered qty: {item.qty}
                  </div>
                </div>
              </label>

              {checked[item._id] ? (
                <div className="mt-3 pl-7">
                  <label className="mb-1 block text-xs text-gray-500">
                    Missing Qty
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={qtyByItem[item._id] ?? ""}
                    onChange={(e) => setQty(item._id, e.target.value)}
                    className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              Your Selfie
            </label>
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => setSelfie(e.target.files?.[0] ?? null)}
              className="w-full text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500">
              Photo of Missing Product
            </label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setProductPhoto(e.target.files?.[0] ?? null)}
              className="w-full text-sm"
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="mt-5 w-full rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    </div>
  );
};

export default DeliveryMissingReportPage;